import { useCallback, useMemo, useRef } from "react";

import ACL_RELATIONSHIPS from "acl-relationships";
import { getNextQueryParams } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import CampaignService from "services/Campaign";
import { useNoticesTable } from "hooks/useNoticesTable";
import { omitByNil } from "helpers/tables";
import { NoticeTableActionButtons } from "routes/campaign/components/notices_table/NoticeTableActionButtons";
import { ResponsesModal } from "routes/campaign/components/notices_table/ResponsesModal";

export const NOTICES_TABLE_CONSTANTS = {
  tableName: "notices",
  className: "CampaignTargetEmail",
  exportTableName: "CampaignTargetEmailsTable",
  header: null,
  headerIcon: null,
};

const NoticesTableV3 = ({ entityId = null, campaignId = null }) => {
  const enableTags = useAuthorized(
    [
      ACL_RELATIONSHIPS.emailTags.create,
      ACL_RELATIONSHIPS.emailTags.read,
      ACL_RELATIONSHIPS.emailTags.delete,
    ],
    { partialMatch: true }
  );
  const responsesModalRef = useRef(null);

  const handleOpenResponsesModal = useCallback((responses, ids) => {
    responsesModalRef.current.open(responses, ids);
  }, []);

  const { filters, columns, queryKey, invalidateData, deleteNotices } =
    useNoticesTable({
      entityId,
      campaignId,
      className: NOTICES_TABLE_CONSTANTS.className,
      tableName: NOTICES_TABLE_CONSTANTS.tableName,
      handleOpenResponsesModal,
    });

  const defaultQueryParams = useMemo(
    () =>
      omitByNil({
        entity_id: entityId,
      }),
    [entityId]
  );
  const downloadParams = useMemo(
    () => omitByNil({ campaign_id: campaignId, entity_id: entityId }),
    [campaignId, entityId]
  );

  const fetchNotices = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);

        let response = await CampaignService.getCampaignEmails(campaignId, {
          ...nextQueryParams,
          ...defaultQueryParams,
        });

        return response;
      } catch (error) {
        console.error("Error fetching notices:", error);
        throw error;
      }
    },
    [columns, campaignId, defaultQueryParams]
  );

  const actionButtons = useCallback(
    ({ selectedIds, selectedData, uncheckAll }) => (
      <NoticeTableActionButtons
        selectedIds={selectedIds}
        selectedData={selectedData}
        uncheckAll={uncheckAll}
        invalidateData={invalidateData}
        deleteNotices={deleteNotices}
      />
    ),
    [invalidateData, deleteNotices]
  );

  return (
    <>
      <ResponsesModal ref={responsesModalRef} />
      <BaseTableV3
        columns={columns}
        filters={filters}
        tagsDropdownParams={{ visible: enableTags }}
        queryKey={queryKey}
        className={NOTICES_TABLE_CONSTANTS.className}
        exportTableName={NOTICES_TABLE_CONSTANTS.exportTableName}
        tableName={NOTICES_TABLE_CONSTANTS.tableName}
        downloadParams={downloadParams}
        enableSearch
        enableSettings
        fetchData={fetchNotices}
        actionButtons={actionButtons}
        invalidateData={invalidateData}
      />
    </>
  );
};

export default NoticesTableV3;
